/**
 * Created by katarinababic on 17.11.22.
 */
import { makeAutoObservable } from 'mobx';
import { EarnModelHistoryRequest, createEarnModelHistoryRequest } from './EarnModelHistoryRequest';
import { Pageable } from '../../Interfaces/Pageable';
import { HttpBackend } from '../../../Services/Http/HttpBackend';
import { Audited, createAuditedFields } from '../../Audited';
import { Media } from '../../Media/Media';

export type EarnModelHistoryQueryRequest = Pageable & {
  startDateTime?: Date;
  endDateTime?: Date;
  allowInChallenge?: boolean;
};

export type EarnModelHistory = Audited & {
  activationState: string;
  allowInChallenge?: boolean;
  athleteId: string;
  customerId: string;
  earnModelId: string;
  gymId: string;
  ledgerId: string;
  pointsCollected: number;
  processedAt: Date;
  request: EarnModelHistoryRequest;
  rewardLink?: string;
  ruleId: string;
  ruleIdentifier: string;
  totalPointsCollected: number;
  singleResults: boolean;
  // Enriched display properties from backend
  title?: string;
  description?: string;
  media?: Media;
  eventType?: string;
  typeName?: string;
};

/**
 * Resolve a value that may be a plain string, a {lang, value} object,
 * or an array of {lang, value} objects into a plain string.
 */
const localizedLangPriority = ['en', 'de', 'fr', 'it'];

function resolveLocalized(val: unknown, fallback?: string): string | undefined {
  if (typeof val === 'string') return val;
  if (!val) return fallback;

  if (Array.isArray(val)) {
    if (val.length === 0) return fallback;
    for (const lang of localizedLangPriority) {
      const match = val.find((v: any) => v?.lang === lang && v?.value);
      if (match) return match.value;
    }
    const first = val.find((v: any) => v?.value);
    return first?.value ?? fallback;
  }

  if (typeof val === 'object' && 'value' in (val as any)) {
    return (val as any).value ?? fallback;
  }

  return String(val);
}

export function createEarnModelHistory(json?: Partial<EarnModelHistory>): EarnModelHistory {
  const audited = createAuditedFields(json);
  return makeAutoObservable({
    ...audited,
    activationState: json?.activationState ?? '',
    allowInChallenge: json?.allowInChallenge,
    athleteId: json?.athleteId ?? '',
    customerId: json?.customerId ?? '',
    earnModelId: json?.earnModelId ?? '',
    gymId: json?.gymId ?? '',
    ledgerId: json?.ledgerId ?? '',
    pointsCollected: json?.pointsCollected ?? 0,
    processedAt: json?.processedAt ? new Date(json.processedAt as any) : new Date(),
    request: createEarnModelHistoryRequest(json?.request as any),
    rewardLink: json?.rewardLink,
    ruleId: json?.ruleId ?? '',
    ruleIdentifier: json?.ruleIdentifier ?? '',
    totalPointsCollected: json?.totalPointsCollected ?? 0,
    singleResults: json?.singleResults ?? false,
    title: resolveLocalized(json?.title),
    description: resolveLocalized(json?.description),
    media: json?.media,
    eventType: json?.eventType,
    typeName: resolveLocalized(json?.typeName),
  });
}

export const EarnModelHistoryApi = {
  find(params: EarnModelHistoryQueryRequest): Promise<EarnModelHistory[]> {
    return HttpBackend.get(`/coach-program/engagement/earn/history`, params).then((json) =>
      (json ?? []).map((e: any) => createEarnModelHistory(e)),
    );
  },

  findChallenge(challengeId: string, params: Pageable): Promise<EarnModelHistory[]> {
    return HttpBackend.get(`/coach-program/engagement/earn/history/challenge/${challengeId}`, params).then((json) =>
      (json ?? []).map((e: any) => createEarnModelHistory(e)),
    );
  },
};
